import { useState } from "react";
import { prisma } from "lib/prisma";
import { GetStaticPaths, GetStaticProps } from "next";
import Slider from "components/Slider";
import CartCount from "components/CartCount";
import classnames from "classnames";
import { useRouter } from "next/router";

import Modal from "components/Modal";
import { PrimaryButton } from "components/ButtonMenu/Button";
import {
  ProductWithRelations,
} from "Types";
import { ProductVariantOption } from ".prisma/client";
import VariantSel from "components/VariantSelection";
import { useMenuContext } from "context/MenuContext";
import getImageUrl from "helpers/getImageUrl";
import { getTranslation } from "dictionary";
import { useUserContext } from "context/UserContext";
import { ToastContainer } from "react-toastify";
import { getFormatedPrice, parse } from "helpers";
import ProductNavBar from "components/NavBars/ProductNavBar";
import { useStylesContext } from "context/StylesContext";
import {
  getAccountWithRelations,
  reduceAccount,
} from "helpers/api/getReducedAccount";
import { useProduct } from "hooks/useProduct";

interface Props {
  product: ProductWithRelations;
}

const Page: React.FC<Props> = ({ product }) => {
  const router = useRouter();
  const { language } = useUserContext();
  const { currency_symbol, features, navbarDesign } = useMenuContext();

  const backgroundColor = navbarDesign.backgroundColor;
  const textColor = navbarDesign.textColor;
  const { windowSize } = useStylesContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const isModalAllowed = windowSize.innerWidth < 640;
  const {
    variantSelection,
    handleOptionSelection,
    count,
    changeCount,
    handleAddToCart,
    totalPrice,
  } = useProduct(product, features);
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleOpenModal = () =>
    windowSize.innerWidth < 640 ? setIsModalOpen(true) : null;

  const slides = () =>
    product.images?.map((image) => {
      return (
        <div
          key={image.image_id}
          id={image.image_id.toString()}
          className="keen-slider__slide"
        >
          <img
            onClick={handleOpenModal}
            src={getImageUrl(image.url, 1000)}
            alt={product.title + " imagen " + image.position}
            width={390}
            height={390}
            className="object-cover w-full aspect-square"
          />
        </div>
      );
    });

  const addToCartButtonCx = classnames({
    "w-full py-4 text-base font-medium tracking-wider leading-tight uppercase rounded-lg shadow-md hover:bg-gray-800 hover:shadow-lg focus:bg-gray-900 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-gray-800 active:shadow-lg transition duration-150 ease-in-out ":
      true,
    "disabled:opacity-50 disabled:cursor-not-allowed": true,
  });

  return (
    <div className="md:h-screen md:flex md:justify-center md:items-center md:bg-gray-50">
      <div className="md:w-[390px] md:mx-auto md:h-[90vh] md:overflow-scroll md:border-4 md:border-black md:rounded-2xl">
        <main id="app_main">
          <div className="h-full bg-white scroll-smooth">
            <ProductNavBar />
            {product.images[0] ? (
              <Slider
                slides={slides()}
                onSlideChange={(slider) =>
                  setCurrentSlide(slider.track.details.rel)
                }
              />
            ) : null}
            <div className="p-4">
              <h1 className="uppercase font-medium text-lg">
                {product.title}
              </h1>
              <div className="flex flex-wrap gap-2 sm:gap-4">
                <h2
                  className={classnames("text-lg mt-1 font-light", {
                    // "font-medium": !product.price_promotional,
                    "line-through text-gray-500": product.price_promotional,
                  })}
                >
                  {getFormatedPrice(product.price, currency_symbol)}
                </h2>
                {product.price_promotional && (
                  <h2 className="text-lg font-light mt-1">
                    {getFormatedPrice(
                      product.price_promotional,
                      currency_symbol
                    )}
                  </h2>
                )}
              </div>

              {product.description && (
                <p className="font-light my-4 whitespace-pre-line">
                  {product.description}
                </p>
              )}
              {product.variants?.map((variant) => (
                <VariantSel
                  key={variant.id}
                  selectedOption={variantSelection[variant.id]}
                  variant={variant}
                  handleSelection={(option: ProductVariantOption) =>
                    handleOptionSelection(variant, option)
                  }
                />
              ))}

              {product.status === "not_available" ? (
                <div className="font-light my-4">
                  {getTranslation(
                    "menu_product",
                    "PRODUCT_NOT_AVAILABLE",
                    language
                  )}
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center my-8">
                    <div className="text-lg flex items-center">
                      <span>Total</span>
                      <span className="text-lg ml-4">
                        {getFormatedPrice(totalPrice, currency_symbol)}
                      </span>
                    </div>
                    <CartCount
                      size="big"
                      changeCount={changeCount}
                      count={count}
                    />
                  </div>
                </>
              )}

              <div className="flex justify-center">
                {product.status === "not_available" ||
                features === "CATALOGO" ? (
                  <PrimaryButton
                    ariaLabel="Back to menu"
                    onClick={() => router.back()}
                  >
                    {getTranslation(
                      "menu_product",
                      "BACK_TO_MENU",
                      language
                    )}
                  </PrimaryButton>
                ) : (
                  <>
                    <button
                      onClick={handleAddToCart}
                      aria-label="Add to cart"
                      className={addToCartButtonCx}
                      style={{ backgroundColor, color: textColor }}
                    >
                      {getTranslation(
                        "menu_product",
                        "ADD_TO_CART",
                        language
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          <Modal
            isOpen={isModalOpen && isModalAllowed}
            onClose={() => setIsModalOpen(false)}
          >
            <div className="h-4/6 mb-8" onClick={() => setIsModalOpen(false)}>
              <Slider
                initialSlide={currentSlide}
                slides={product.images?.map((image) => (
                  <div
                    key={image.image_id}
                    className="keen-slider__slide px-2 my-auto"
                  >
                    <img
                      src={getImageUrl(image.url, 1000)}
                      alt={image.filename + "image"}
                      className="object-cover w-full rounded-lg"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                ))}
              />
            </div>
          </Modal>
        </main>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default Page;

export const getStaticPaths: GetStaticPaths = async () => {
  const accounts = await prisma.account.findMany({
    where: {
      slug: { not: null },
      categories: { some: { id: { not: undefined } } },
    },
    include: { products: { where: { removed: false } } },
  });

  const productUrls: string[] = [];
  accounts.forEach((account) => {
    account.products.forEach((product) => {
      productUrls.push(`/${account.slug}/${product.slug}`);
    });
  });
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  if (
    typeof params?.store_name !== "string" ||
    typeof params?.slug !== "string"
  ) {
    return {
      notFound: true,
    };
  }
  const account = await getAccountWithRelations(params.store_name);
  if (!account) {
    return {
      notFound: true,
    };
  }

  const product: ProductWithRelations | null = await prisma.product.findFirst({
    where: {
      userId: account.id,
      slug: params.slug,
      removed: false,
    },
    include: {
      images: {
        orderBy: {
          position: "asc",
        },
      },
      variants: {
        where: { NOT: { removed: true } },
        orderBy: { position: "asc" },
        include: {
          options: {
            where: { NOT: { removed: true } },
            orderBy: { position: "asc" },
          },
        },
      },
      extras: {
        include: {
          extraItems: {
            include: {
              product: {
                include: {
                  images: true,
                },
              },
            },
          },
        },
      },
    },
  });

  if (!product)
    return {
      notFound: true,
    };
  return {
    props: {
      product: parse(product),
      SEODescription: product.description,
      SEOTitle: `${product.title} by ${account.business_name}`,
      SEOImage: product.images[0]?.url || null,
      account: reduceAccount(account),
    },
  };
};
